// Header entity selector: a dropdown replacing the static "All Entities" pill
// for users who can see more than one entity. Options are 'All Entities'
// plus each entity in `allowedEntities`. Selection is written to
// EntityFilterContext, which persists it to localStorage.
//
// The menu panel is rendered with position: fixed anchored to the trigger's
// bounding rect. This intentionally escapes the main-column overflow:hidden
// wrapper (Layout.view.jsx:160) that would otherwise clip an
// absolutely-positioned menu, and sits above the mobile sidebar (z-50) via
// z-[1000].

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CaretDown, Check } from '@phosphor-icons/react';
import { ENTITY } from '../../lib/statusConfig';
import { useEntityFilter } from '../../context/EntityFilterContext';

// Render a compact icon + label for a given entity slug. Falls back to the
// bare slug when it isn't in the design-token catalogue (e.g. a future entity
// that hasn't been styled yet).
function EntityRow({ slug }) {
  const cfg = ENTITY[slug];
  if (!cfg) return <span className="text-ink">{slug}</span>;
  const { Icon, label } = cfg;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon size={12} weight="fill" /> {label}
    </span>
  );
}

export function EntitySelector() {
  const { selectedEntity, setSelectedEntity, allowedEntities, showSelector } = useEntityFilter();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Position the menu just below the trigger, anchored to its right edge, in
  // viewport coordinates (fixed positioning). Recomputed on open and on any
  // scroll/resize that could move the trigger.
  const reposition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuPos({
      top: r.bottom + 4,                       // 4px gap under trigger
      right: Math.max(0, window.innerWidth - r.right),
    });
  };
  useLayoutEffect(() => { if (open) reposition(); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onScroll = () => reposition();
    window.addEventListener('scroll', onScroll, true); // capture: any scrolling ancestor
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  // Close on outside click (checks both the trigger and the fixed-positioned
  // menu, since they aren't DOM-nested) and on Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const t = triggerRef.current, m = menuRef.current;
      if (t && t.contains(e.target)) return;
      if (m && m.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!showSelector) return null;

  const current = selectedEntity === 'all'
    ? { label: 'All Entities', slug: null }
    : { label: (ENTITY[selectedEntity]?.label || selectedEntity), slug: selectedEntity };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-chip bg-paper-muted px-2.5 py-1 text-[11px] font-semibold text-ink hover:bg-paper-border2/60 focus:outline-none">
        {current.slug ? <EntityRow slug={current.slug} /> : <span>All Entities</span>}
        <CaretDown size={10} weight="bold" className="opacity-60" />
      </button>

      {open && (
        // z-[1000] to sit above the sidebar (z-50) and avatar menu (z-30).
        // position:fixed escapes the main-column overflow:hidden that would
        // otherwise clip an absolute-positioned dropdown.
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
          className="z-[1000] min-w-[180px] rounded-card border border-paper-border2 bg-paper py-1 shadow-lg">
          <button
            type="button"
            onClick={() => { setSelectedEntity('all'); setOpen(false); }}
            className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[12px] text-ink hover:bg-paper-muted">
            <span>All Entities</span>
            {selectedEntity === 'all' && <Check size={12} weight="bold" />}
          </button>
          {allowedEntities.map((e) => (
            <button
              key={e.slug}
              type="button"
              onClick={() => { setSelectedEntity(e.slug); setOpen(false); }}
              className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[12px] text-ink hover:bg-paper-muted">
              <EntityRow slug={e.slug} />
              {selectedEntity === e.slug && <Check size={12} weight="bold" />}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

export default EntitySelector;
